/**
 * AppSwitcher — header chip for Attuned members.
 *
 * Shows the currently-active community and lets them flip between the three.
 * Hidden for non-Attuned tiers (their plan is community-locked).
 */
import { ChevronsUpDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { APPS, type AppId } from "@/lib/apps";
import { useProfile } from "@/lib/profile/useProfile";
import { toast } from "sonner";

const AppSwitcher = () => {
  const { profile, update } = useProfile();
  if (!profile) return null;
  const isAttuned = profile.tier === "tier_attuned";
  if (!isAttuned) return null;

  const activeId = (profile.active_app as AppId) ?? (profile.primary_app as AppId);
  const active = APPS.find((a) => a.id === activeId) ?? APPS[0];

  const switchTo = async (id: AppId) => {
    if (id === activeId) return;
    const { error } = await update({ active_app: id });
    if (error) toast.error(error);
    else toast.success(`Switched to ${APPS.find((a) => a.id === id)?.name}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2.5 py-1 text-xs font-medium hover:bg-secondary transition-colors"
          aria-label="Switch community"
        >
          <img src={active.icon} alt="" className="h-3.5 w-3.5" />
          <span>{active.name}</span>
          <ChevronsUpDown className="h-3 w-3 text-muted-foreground" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {APPS.map((a) => (
          <DropdownMenuItem
            key={a.id}
            onClick={() => switchTo(a.id)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <img src={a.icon} alt="" className="h-4 w-4" />
            <span className="flex-1">{a.name}</span>
            {a.id === activeId && (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Active
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AppSwitcher;
